
import { Box } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import React, { useContext } from "react";
import {
  Text,
  Badge,
  Spacer,
  useTheme,
  Card,
  Link,
  Avatar,
  Grid
} from "@geist-ui/core";
import { Check } from "@geist-ui/icons";
import { InvestmentContextFull } from "../../pages/direct/investment-process-v2/investment-process-full";

type VerticalStepperProps = {
  totalSteps: number;
  style?: React.CSSProperties;
  blockTo?: number;
  activeStep: number;
  highestStep: number;
  labels: string[];
  returnOnFirstStep?: boolean;
  onStepClick: (step: number) => void;
  hideFirstStep?: boolean;
  alignTop?: boolean;
  loading?: boolean;
};

export default function VerticalStepperGeist({
  totalSteps,
  style,
  activeStep,
  highestStep,
  labels,
  blockTo,
  returnOnFirstStep,
  onStepClick,
  hideFirstStep,
  loading,
}: VerticalStepperProps) {
  const theme = useTheme();
  
  // Calculate progress for mobile view
  const progress = ((activeStep + 1) / totalSteps) * 100;

  return (
    <>
      {/* Desktop View */}
      <Box
        sx={{
          display: {
            xs: "none",
            md: "flex",
          },
        }}
        style={{
          ...style,
        }}
        className="flex-col gap-8"
      >
        <div className="flex flex-col gap-1">
          {labels.map((label, i) => {
            if (loading)
               return (
                  <div
                     key={i}
                     className="animate-pulse h-10 w-full bg-gray-100 rounded-lg opacity-50 mb-2"
                  />
               );
            
             if (hideFirstStep && i === 0) return null;
             return (
              <StepperEntryGeist
                key={i}
                onStepClick={(index) => onStepClick(index)}
                text={label}
                step={i}
                activeStep={activeStep}
                highestStep={highestStep}
                totalSteps={labels.length}
                blockTo={blockTo}
              />
            );
          })}
        </div>

        {/* Minimal Support Section */}
        <div className="w-full mt-12 pt-8 border-t border-gray-100">
            <div className="flex flex-col gap-5">
               <div className="flex gap-4 items-center">
                  <Avatar src="https://ik.imagekit.io/inventure/Staff/sg_square_WVoAqSytS?updatedAt=1726823657325" scale={1.5} />
                  <div className="flex flex-col">
                     <Text b style={{ fontSize: '0.85rem', margin: 0 }}>Hilfe benötigt?</Text>
                     <Text small type="secondary" style={{ fontSize: '0.75rem', margin: 0, color: theme.palette.accents_5 }}>Wir unterstützen Sie gerne.</Text>
                  </div>
               </div>
               
               <div className="flex gap-4">
                  <Link 
                     href="#" 
                     onClick={(e) => {
                        e.preventDefault();
                        if ((window as any).HubSpotConversations) {
                           (window as any).HubSpotConversations.widget.open();
                        }
                     }}
                     style={{ fontSize: '0.85rem', fontWeight: 500, color: theme.palette.foreground }}
                  >
                     Chat
                  </Link>
                  <Link 
                     href="https://meetings.hubspot.com/dat-le" 
                     target="_blank" 
                     style={{ fontSize: '0.85rem', fontWeight: 500, color: theme.palette.foreground }}
                  >
                     Termin buchen
                  </Link>
               </div>
            </div>
        </div>
      </Box>

      {/* Mobile View */}
      <Box sx={{ display: { xs: "block", md: "none" } }} style={{ width: '100%', marginBottom: '24px' }}>
          <div className="flex flex-col gap-3 pt-4">
             <div className="flex justify-between items-center">
                <Text small b style={{ textTransform: 'uppercase', color: theme.palette.accents_5, fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                   Schritt {activeStep + 1} von {totalSteps}
                </Text>
                <Text b style={{ fontSize: '0.9rem' }}>{labels[activeStep]}</Text>
             </div>
             <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <motion.div 
                   className="h-full bg-black"
                   initial={{ width: 0 }}
                   animate={{ width: `${progress}%` }}
                   transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} // smooth ease-out
                />
             </div>
             {activeStep > 0 && (
                <div 
                   className="flex items-center gap-1 text-xs font-semibold text-gray-400 cursor-pointer mt-1"
                   onClick={() => onStepClick(activeStep - 1)}
                >
                   ← ZURÜCK
                </div>
             )}
          </div>
      </Box>
    </>
  );
}

type StepperEntryProps = {
  text: string;
  step: number;
  blockTo?: number;
  activeStep: number;
  highestStep: number;
  onStepClick: (step: number) => void;
};

function StepperEntryGeist({
  text,
  step,
  activeStep,
  highestStep,
  totalSteps,
  blockTo,
  onStepClick,
}: StepperEntryProps & { totalSteps: number }) {
  const theme = useTheme();
  
  if (blockTo === undefined) blockTo = 0;
  
  const isActive = activeStep === step;
  const isCompleted = step < activeStep;
  const isAccessible = step <= highestStep;
  const isBlocked = step < blockTo; 
  const canClick = !isBlocked && isAccessible;

  return (
    <div className="relative flex gap-4 min-h-[48px]">
      {/* Vertical Line Connector (True Continuous) */}
      <div className="flex flex-col items-center shrink-0 w-6">
         {/* Line above (not for first step) */}
         <div 
            className="w-[1.5px] flex-grow"
            style={{ 
               visibility: step === 0 ? 'hidden' : 'visible',
               backgroundColor: isCompleted || isActive ? theme.palette.foreground : theme.palette.accents_2,
               transition: 'background-color 0.3s ease'
            }}
         />
         
         {/* Indicator Circle */}
         <div
            className={`flex items-center justify-center rounded-full border transition-all duration-300 my-1`}
            style={{
               width: '24px',
               height: '24px',
               borderColor: isActive || isCompleted ? theme.palette.foreground : theme.palette.accents_2,
               backgroundColor: isCompleted || isActive ? theme.palette.foreground : 'transparent',
               zIndex: 2,
               transform: isActive ? 'scale(1.1)' : 'scale(1)'
            }}
         >
            {isCompleted ? (
               <Check size={12} color={theme.palette.background} />
            ) : (
               <Text 
                  span 
                  style={{ 
                     fontSize: '11px', 
                     fontWeight: 700, 
                     color: isActive ? theme.palette.background : theme.palette.accents_4 
                    }}
                 >
                    {step + 1}
                 </Text>
              )}
           </div>

         {/* Line below (not for last step) */}
         <div 
            className="w-[1.5px] flex-grow"
            style={{ 
               visibility: step === totalSteps - 1 ? 'hidden' : 'visible',
               backgroundColor: isCompleted ? theme.palette.foreground : theme.palette.accents_2,
               transition: 'background-color 0.3s ease'
            }}
         />
      </div>

      <motion.div 
        onClick={() => {
            if (canClick) {
                onStepClick(step);
            }
        }}
        whileHover={canClick && !isActive ? { x: 4 } : {}}
        className={`flex flex-grow items-center gap-4 py-2 px-2 -ml-2 rounded-lg transition-all duration-200 
            ${canClick && !isActive ? 'cursor-pointer hover:bg-gray-50' : isActive ? 'cursor-default' : 'cursor-not-allowed'}`}
      >
           <Text 
              span 
              style={{ 
                 color: isActive ? theme.palette.foreground : canClick ? theme.palette.accents_7 : theme.palette.accents_4,
                 fontWeight: isActive ? 600 : 500,
                 fontSize: '0.9rem',
                 transition: 'color 0.2s',
                 letterSpacing: '-0.01em'
              }}
           >
              {text}
           </Text>
           {/* Optional: Subtle indicator for clickability if not active */}
           {canClick && !isActive && !isCompleted && (
              <div className="w-1 h-1 rounded-full bg-gray-300 ml-auto" />
           )}
      </motion.div>
    </div>
  );
}
